import { ClipboardStatus } from "@/types/copy-paste";

declare global {
  // @see https://github.com/microsoft/TypeScript/issues/33923
  interface PermissionDescriptor {
    allowWithoutGesture: boolean;
  }
}

export async function maybeQueryClipboardPermission(): Promise<PermissionStatus | null> {
  try {
    return await navigator.permissions.query({
      name: "clipboard-read" as PermissionName, // @see https://github.com/microsoft/TypeScript/issues/33923
      allowWithoutGesture: false
    });
  } catch (e) {
    return null;
  }
}

export async function getClipboardStatus(): Promise<ClipboardStatus> {
  const permission = await maybeQueryClipboardPermission();

  return {
    secure: window.isSecureContext,
    permission
  };
}

export function fallbackWriteToClipboard(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  textArea.classList.add("breakdance-offscreen-input");

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  let result: boolean;

  try {
    result = document.execCommand("copy");
  } catch (err) {
    result = false;
  }

  document.body.removeChild(textArea);
  return result;
}

function isHTMLInputElement(node: HTMLElement): node is HTMLInputElement {
  return node?.nodeName === "INPUT";
}

function isTextField(node: HTMLElement) {
  const nonTextInputs = [
    "button",
    "checkbox",
    "hidden",
    "file",
    "radio",
    "image",
    "range",
    "reset",
    "submit",
    "number",
    "email",
    "time"
  ];

  return (
    (isHTMLInputElement(node) &&
      node.type &&
      !nonTextInputs.includes(node.type)) ||
    node.nodeName === "TEXTAREA" ||
    node.contentEditable === "true"
  );
}

export function documentHasTextSelection(doc: Document) {
  const selection = doc?.defaultView?.getSelection();
  const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
  return !!range && !range.collapsed;
}

export function documentHasSelection(doc: Document) {
  const activeEl = doc.activeElement as HTMLElement;

  return (
    activeEl &&
    (isHTMLInputElement(activeEl) ||
      isTextField(activeEl) ||
      documentHasTextSelection(doc))
  );
}
